import { useState, useMemo } from 'react'
import { Link2, Loader2, Users, Search, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useToast } from '@/hooks/use-toast'
import {
  useCompanies,
  useUnlinkedUsers,
  useLinkUsers,
} from '@/services/super-admin/companies.service'
import { useAdminUsers, type AdminUser } from '@/services/admin/users.service'
import { useDebounce } from '@/hooks/use-debounce'
import { getErrorMessage } from '@/utils/error'

interface LinkUsersDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function LinkUsersDialog({ open, onOpenChange }: LinkUsersDialogProps) {
  const { toast } = useToast()
  const { data: companies } = useCompanies()
  const linkUsers = useLinkUsers()

  // Shared
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(undefined)
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<string>('domain')

  // Domain tab
  const [domainFilter, setDomainFilter] = useState('')

  // Search tab
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearch = useDebounce(searchQuery, 400)

  const activeCompanies = useMemo(() => companies?.filter((c) => c.active) ?? [], [companies])

  const activeCompaniesWithDomain = useMemo(
    () => activeCompanies.filter((c) => c.domains.length > 0),
    [activeCompanies]
  )

  const selectedCompany = useMemo(
    () => activeCompanies.find((c) => c.id === selectedCompanyId),
    [activeCompanies, selectedCompanyId]
  )

  // Domain tab data
  const { data: unlinkedUsers, isLoading: loadingUnlinked } = useUnlinkedUsers(
    activeTab === 'domain' ? selectedCompanyId : undefined
  )

  const filteredUnlinked = useMemo(() => {
    if (!unlinkedUsers) return []
    if (!domainFilter) return unlinkedUsers
    const s = domainFilter.toLowerCase()
    return unlinkedUsers.filter(
      (u) => u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s)
    )
  }, [unlinkedUsers, domainFilter])

  const allDomainSelected =
    filteredUnlinked.length > 0 && filteredUnlinked.every((u) => selectedUserIds.has(u.id))

  // Search tab data
  const { data: searchResults, isLoading: loadingSearch } = useAdminUsers(
    debouncedSearch.length >= 3 ? debouncedSearch : undefined
  )

  const searchableUsers = useMemo(() => {
    if (!searchResults || !selectedCompanyId) return []
    return searchResults.filter((u) => u.companyId !== selectedCompanyId)
  }, [searchResults, selectedCompanyId])

  // Helpers
  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const toggleAllDomain = () => {
    if (allDomainSelected) {
      const next = new Set(selectedUserIds)
      filteredUnlinked.forEach((u) => next.delete(u.id))
      setSelectedUserIds(next)
    } else {
      const next = new Set(selectedUserIds)
      filteredUnlinked.forEach((u) => next.add(u.id))
      setSelectedUserIds(next)
    }
  }

  const handleCompanyChange = (value: string) => {
    setSelectedCompanyId(value)
    setSelectedUserIds(new Set())
    setDomainFilter('')
    setSearchQuery('')
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setSelectedUserIds(new Set())
  }

  const handleLink = async () => {
    if (!selectedCompanyId || selectedUserIds.size === 0) return

    try {
      await linkUsers.mutateAsync({
        companyId: selectedCompanyId,
        userIds: Array.from(selectedUserIds),
      })

      toast({
        title: 'Usuários vinculados',
        description: `${selectedUserIds.size} usuário${selectedUserIds.size !== 1 ? 's' : ''} vinculado${selectedUserIds.size !== 1 ? 's' : ''} à ${selectedCompany?.name}.`,
      })

      setSelectedUserIds(new Set())
      setSelectedCompanyId(undefined)
      setDomainFilter('')
      setSearchQuery('')
      onOpenChange(false)
    } catch (err: unknown) {
      toast({
        title: 'Erro ao vincular',
        description: getErrorMessage(err, 'Ocorreu um erro ao vincular os usuários.'),
        variant: 'destructive',
      })
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedCompanyId(undefined)
      setSelectedUserIds(new Set())
      setDomainFilter('')
      setSearchQuery('')
      setActiveTab('domain')
    }
    onOpenChange(open)
  }

  const renderUserRow = (
    user: { id: string; name: string; email: string; avatar?: string | null },
    extra?: React.ReactNode
  ) => (
    <label
      key={user.id}
      className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
    >
      <Checkbox
        checked={selectedUserIds.has(user.id)}
        onCheckedChange={() => toggleUser(user.id)}
      />
      <Avatar className="h-8 w-8">
        <AvatarImage src={user.avatar ?? undefined} />
        <AvatarFallback className="text-xs">{user.name.charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{user.name}</p>
        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
      </div>
      {extra}
    </label>
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            Vincular Usuários a Empresa
          </DialogTitle>
          <DialogDescription>
            Selecione uma empresa e escolha os usuários que deseja vincular.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Company selector */}
          <Select value={selectedCompanyId ?? ''} onValueChange={handleCompanyChange}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione uma empresa..." />
            </SelectTrigger>
            <SelectContent>
              {activeCompanies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                  {company.domains.length > 0
                    ? ` — ${company.domains.map((d) => `@${d.domain}`).join(', ')}`
                    : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedCompanyId && (
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="w-full">
                <TabsTrigger
                  value="domain"
                  className="flex-1"
                  disabled={!selectedCompany?.domains?.length}
                >
                  Por domínio
                </TabsTrigger>
                <TabsTrigger value="search" className="flex-1">
                  Buscar usuário
                </TabsTrigger>
              </TabsList>

              {/* Domain tab */}
              <TabsContent value="domain" className="space-y-3 mt-3">
                {loadingUnlinked ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : !unlinkedUsers || unlinkedUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">
                      Nenhum usuário sem empresa com os domínios de {selectedCompany?.name}.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Filtrar por nome ou email..."
                        value={domainFilter}
                        onChange={(e) => setDomainFilter(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    <div className="flex items-center gap-2 px-1">
                      <Checkbox
                        checked={allDomainSelected}
                        onCheckedChange={toggleAllDomain}
                        id="select-all-domain"
                      />
                      <label
                        htmlFor="select-all-domain"
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        Selecionar todos ({filteredUnlinked.length})
                      </label>
                      {selectedUserIds.size > 0 && (
                        <span className="ml-auto text-sm text-primary font-medium">
                          {selectedUserIds.size} selecionado{selectedUserIds.size !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    <ScrollArea className="max-h-[280px] rounded-md border border-border/50">
                      <div className="divide-y divide-border/50">
                        {filteredUnlinked.map((user) => renderUserRow(user))}
                      </div>
                    </ScrollArea>
                  </>
                )}
              </TabsContent>

              {/* Search tab */}
              <TabsContent value="search" className="space-y-3 mt-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Buscar por nome ou email (min. 3 caracteres)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {loadingSearch && debouncedSearch.length >= 3 ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : debouncedSearch.length < 3 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Digite pelo menos 3 caracteres para buscar.</p>
                  </div>
                ) : searchableUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhum usuário encontrado.</p>
                  </div>
                ) : (
                  <>
                    {selectedUserIds.size > 0 && (
                      <div className="px-1">
                        <span className="text-sm text-primary font-medium">
                          {selectedUserIds.size} selecionado{selectedUserIds.size !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}

                    <ScrollArea className="max-h-[280px] rounded-md border border-border/50">
                      <div className="divide-y divide-border/50">
                        {searchableUsers.map((user) =>
                          renderUserRow(
                            user,
                            user.company ? (
                              <Badge variant="outline" className="shrink-0 text-xs gap-1">
                                <Building2 className="h-3 w-3" />
                                {user.company.name}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="shrink-0 text-xs">
                                Sem empresa
                              </Badge>
                            )
                          )
                        )}
                      </div>
                    </ScrollArea>
                  </>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleLink}
            disabled={selectedUserIds.size === 0 || linkUsers.isPending || !selectedCompanyId}
            className="gap-2"
          >
            {linkUsers.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Vinculando...
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4" />
                Vincular {selectedUserIds.size > 0 ? `(${selectedUserIds.size})` : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
